import {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import {
  isColorRangeFormatting,
  isListFieldFormatting,
  isRelativeDateValue,
} from '@ardoq/api-types';
import {
  assignColorRangeColors,
  assignListColors,
} from './getFormattingNextColor';
import {
  COLOR_RANGE_KIND,
  ColorRangeFormatting,
  FilterInfoOperator,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import {
  mapFilterGroups,
  mapFormatting,
  maybeGetInitialListFieldFormatting,
} from './mapFilterGroups';
import type { Formatting, FormattingGroup } from './types';
import { isNil, omit } from 'lodash';
import { isLegacyFormattingRule } from '../../utils';
import { ensureRelativeDateValue } from '@ardoq/query-builder';
import type { I18nLocale } from '@ardoq/i18n';

const DEFAULT_COLOR_RANGE: ColorRangeFormatting = {
  kind: COLOR_RANGE_KIND,
  mode: 'percent',
  min: { value: 0, color: '' },
  mid: { color: '' },
  max: { value: 100, color: '' },
};

/* Returns a valid ColorRangeFormatting object, if the provided value is not of that type */
export const ensureColorRangeValue = (
  value: ComponentOrReferenceFormattingRule['value']
): ColorRangeFormatting => {
  if (!isColorRangeFormatting(value)) {
    return DEFAULT_COLOR_RANGE;
  }
  return {
    kind: COLOR_RANGE_KIND,
    mode: value.mode ?? DEFAULT_COLOR_RANGE.mode,
    min: value.min || DEFAULT_COLOR_RANGE.min,
    mid: value.mid,
    max: value.max || DEFAULT_COLOR_RANGE.max,
  };
};

const isComponentOrReferenceFormattingRule = (
  rule: Partial<FormattingRule>
): rule is ComponentOrReferenceFormattingRule =>
  rule.type === 'component' || rule.type === 'reference';

type FieldFormattingData = {
  i18nLocale: I18nLocale;
  updatedFormattingRulePayload: Partial<FormattingRule>;
  componentsFormattingGroups: FormattingGroup[];
  referencesFormattingGroups: FormattingGroup[];
  formattingForInvalidRuleByRuleId: Record<string, Formatting>;
  componentNameAndFieldsIndex: Map<string, string[]>;
  referenceNameAndFieldsIndex: Map<string, string[]>;
  isNewConditionalFormatting: boolean;
  viewId?: string;
};

// Builds the updated field formatting for a given updated rule
function buildUpdatedFieldFormatting(
  fieldFormattingData: FieldFormattingData,
  updatedRule: FormattingRule
): Partial<Record<string, Formatting>> {
  const filterGroupsForNewTypeName = mapFilterGroups({
    conditionalFormattingRules: [updatedRule],
    ...fieldFormattingData,
  });

  const formattingByRuleAndFieldForNewTypeName = mapFormatting(
    filterGroupsForNewTypeName
  );

  return formattingByRuleAndFieldForNewTypeName[updatedRule.__id];
}

type EnsureUpdatedRuleValueArgs = {
  previousFormattingRules: FormattingRule[];
  updatedFormattingRuleId: string;
  updatedFormattingRulePayload: Partial<FormattingRule>;
  ruleFieldFormatting: Partial<Record<string, Formatting>>;
  fieldFormattingData: FieldFormattingData;
};
/**
 * Ensures that the updated formatting rule payload has a valid value,
 * assigning colors where necessary based on previous formatting rules and field formatting.
 */
export function ensureUpdatedRuleValue({
  previousFormattingRules,
  updatedFormattingRulePayload,
  updatedFormattingRuleId,
  ruleFieldFormatting,
  fieldFormattingData,
}: EnsureUpdatedRuleValueArgs): Partial<FormattingRule> {
  if (!ruleFieldFormatting) {
    return updatedFormattingRulePayload;
  }

  const previousRule = previousFormattingRules.find(
    ({ __id }) => __id === updatedFormattingRuleId
  );
  if (!previousRule) {
    return updatedFormattingRulePayload;
  }

  if (!isComponentOrReferenceFormattingRule(previousRule)) {
    return updatedFormattingRulePayload;
  }

  const nextRule = {
    ...previousRule,
    ...omit(updatedFormattingRulePayload, 'type'),
  };

  // If the updated rule is not a component or reference formatting rule, return as is
  if (!isComponentOrReferenceFormattingRule(nextRule)) {
    return updatedFormattingRulePayload;
  }

  const previousRulesWithoutCurrent = previousFormattingRules.filter(
    ({ __id }) => __id !== updatedFormattingRuleId
  );

  // Determine the value to use, defaulting to null if undefined
  let value = isNil(nextRule.value) ? null : nextRule.value;

  // Initialize list field formatting if applicable
  const { field, comparator } = nextRule;
  value =
    !isLegacyFormattingRule(nextRule) &&
    field &&
    comparator === FilterInfoOperator.EQUALS &&
    ruleFieldFormatting[field]?.selectOptions &&
    !isListFieldFormatting(value)
      ? maybeGetInitialListFieldFormatting(nextRule, ruleFieldFormatting[field])
      : value;

  // #region checkbox rule values are always true.
  const shouldSetTrue =
    !isLegacyFormattingRule(nextRule) &&
    nextRule.field &&
    (nextRule.comparator === FilterInfoOperator.BOOLEAN ||
      nextRule.comparator === FormattingRuleOperator.NOT_BOOLEAN);

  value = shouldSetTrue ? true : value;
  // #endregion

  // #region If the comparator is IS_EMPTY, the value should be null
  if (
    nextRule.comparator === FormattingRuleOperator.IS_EMPTY &&
    value !== null
  ) {
    value = null;
  }
  // #endregion

  // Assign colors for list field formatting if needed
  if (
    nextRule.field &&
    !isLegacyFormattingRule(nextRule) &&
    isListFieldFormatting(value) &&
    Object.values(value).some(v => !v?.color)
  ) {
    value = assignListColors(
      nextRule.field,
      value,
      previousRulesWithoutCurrent,
      fieldFormattingData.viewId
    );
  }

  // Ensure color range formatting is valid if applicable
  if (
    'comparator' in updatedFormattingRulePayload &&
    updatedFormattingRulePayload.comparator ===
      FormattingRuleOperator.COLOR_RANGE &&
    !isColorRangeFormatting(value)
  ) {
    value = ensureColorRangeValue(value);
  }

  // Ensure relative date value is valid if applicable
  if (
    'comparator' in updatedFormattingRulePayload &&
    updatedFormattingRulePayload.comparator ===
      FormattingRuleOperator.RELATIVE_DATE &&
    !isRelativeDateValue(value)
  ) {
    value = ensureRelativeDateValue(value);
  }

  // Assign colors for color range formatting if needed
  if (isColorRangeFormatting(value)) {
    const hasAnyColor =
      value.min?.color || value.mid?.color || value.max?.color;

    if (!hasAnyColor) {
      value = assignColorRangeColors(
        previousRulesWithoutCurrent,
        value,
        fieldFormattingData.viewId
      );
    }
  }

  // Make sure that the currently selected field exists for the given type name
  // if not, reset the field-related properties
  let fieldResetPayload: Partial<FormattingRule> = {};
  if (
    'typeNames' in updatedFormattingRulePayload &&
    updatedFormattingRulePayload.typeNames &&
    updatedFormattingRulePayload.typeNames.length > 0 &&
    previousRule.field
  ) {
    const nextRuleFieldFormatting = buildUpdatedFieldFormatting(
      fieldFormattingData,
      nextRule
    );
    const filter = nextRuleFieldFormatting?.[previousRule.field];
    if (!filter) {
      fieldResetPayload = {
        value: null,
        field: null,
        comparator: undefined,
      };
    }
  }

  return {
    ...updatedFormattingRulePayload,
    // provide the updated value only if it has changed
    ...(value !== nextRule?.value ? { value } : {}),
    ...fieldResetPayload,
  } as Partial<FormattingRule>;
}
